/**
 * extension.ts — bridge to the Tabgraph Chrome extension.
 *
 * The SPA can ask the extension what's buffered locally (sessions captured
 * while the user was signed out) via `chrome.runtime.sendMessage(extId, …)`.
 * The extension's manifest declares `externally_connectable` for the SPA's
 * origin, so this works without a content script.
 *
 * Finding the extension ID:
 *   1. URL query param `?tabgraph_ext=<id>` — the popup's "Open in tabgraph"
 *      link injects this. The first arrival stashes the ID in localStorage.
 *   2. localStorage `tabgraph.extensionId` — for subsequent visits.
 *   3. `import.meta.env.VITE_TABGRAPH_EXTENSION_ID` — set at build time once
 *      the production extension ID is known (after Chrome Web Store publish).
 *
 * If none of those resolve, the SPA simply doesn't surface local sessions —
 * users without the extension installed see the normal flow.
 */

const STORAGE_KEY = "tabgraph.extensionId";
const QUERY_PARAM = "tabgraph_ext";

export interface LocalSessionSummary {
  id: string;
  name: string;
  started_at: number;
  ended_at: number | null;
  pendingEventCount: number;
}

// Minimal shape of chrome.runtime.sendMessage as it appears on a regular web
// page when an extension has `externally_connectable` enabled. The full
// type lives in @types/chrome which we don't want as a runtime dep here.
interface ChromeRuntime {
  sendMessage: (
    extensionId: string,
    message: unknown,
    callback?: (response: unknown) => void,
  ) => void;
  lastError?: { message: string };
}
interface ChromeNamespace {
  runtime?: ChromeRuntime;
}
declare global {
  interface Window {
    chrome?: ChromeNamespace;
  }
}

/** Best-effort capture of the extension ID from the current URL.
 *  Removes the query param after capturing so the URL bar stays clean. */
export function captureExtensionIdFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const url = new URL(window.location.href);
  const id = url.searchParams.get(QUERY_PARAM);
  if (!id) return null;
  localStorage.setItem(STORAGE_KEY, id);
  url.searchParams.delete(QUERY_PARAM);
  window.history.replaceState({}, "", url.toString());
  return id;
}

/** Return the cached extension ID, if known. */
export function getExtensionId(): string | null {
  const fromUrl = captureExtensionIdFromUrl();
  if (fromUrl) return fromUrl;
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) return stored;
  const envId = import.meta.env.VITE_TABGRAPH_EXTENSION_ID;
  return envId ? String(envId) : null;
}

/** Forget the cached extension ID. Useful if the user wants to disconnect a
 * particular install. */
export function forgetExtensionId(): void {
  localStorage.removeItem(STORAGE_KEY);
}

function sendMessage<T>(extensionId: string, message: unknown): Promise<T> {
  const r = window.chrome?.runtime;
  if (!r || !r.sendMessage) {
    return Promise.reject(new Error("chrome.runtime not available"));
  }
  return new Promise<T>((resolve, reject) => {
    try {
      r.sendMessage(extensionId, message, (resp: unknown) => {
        const err = r.lastError;
        if (err) {
          reject(new Error(err.message));
          return;
        }
        if (!resp) {
          reject(new Error("no response from extension"));
          return;
        }
        resolve(resp as T);
      });
    } catch (e) {
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

/** Ping the extension to confirm it's installed and reachable. */
export async function pingExtension(): Promise<boolean> {
  const id = getExtensionId();
  if (!id) return false;
  try {
    const resp = await sendMessage<{ ok: boolean }>(id, { type: "PING" });
    return resp?.ok === true;
  } catch {
    return false;
  }
}

/** List every locally-buffered session in the extension's Dexie. Returns an
 * empty array if the extension isn't installed or doesn't have any local
 * sessions. Never throws — callers can render the absence of data. */
export async function listLocalSessions(): Promise<LocalSessionSummary[]> {
  const id = getExtensionId();
  if (!id) return [];
  try {
    const resp = await sendMessage<{ ok: boolean; sessions?: LocalSessionSummary[] }>(
      id,
      { type: "LIST_LOCAL_SESSIONS" },
    );
    return resp?.ok && resp.sessions ? resp.sessions : [];
  } catch {
    return [];
  }
}

/** Ask the extension to flush its buffer to the backend now (rather than
 * waiting for the 30s alarm). Useful right after sign-in. */
export async function flushExtensionNow(): Promise<boolean> {
  const id = getExtensionId();
  if (!id) return false;
  try {
    const resp = await sendMessage<{ ok: boolean }>(id, { type: "FLUSH_NOW" });
    return resp?.ok === true;
  } catch {
    return false;
  }
}
